import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLoadingModal } from "./ScanLoadingModal";
import { ScanFailedModal } from "./ScanFailedModal";
import { importCaseStudyFromUrl } from "@/services/caseStudy/importService";
import { CaseStudyItem } from "@/types/case-study";

interface ImportCaseStudyModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (url: string) => void;
  onImportSuccess?: (data: Partial<CaseStudyItem>) => void;
}

type ScanState = "idle" | "scanning" | "failed" | "success";

interface ImportCaseStudyFormProps {
  onBack: () => void;
  onImport: (url: string) => void;
  onImportSuccess?: (data: Partial<CaseStudyItem>) => void;
}

export const ImportCaseStudyForm: React.FC<ImportCaseStudyFormProps> = ({
  onBack,
  onImport,
  onImportSuccess,
}) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [errorDetails, setErrorDetails] = useState("");
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(1);

  const validateURL = (value: string) => {
    try {
      const parsed = new URL(value);
      const validPatterns = [
        /portfolio\.com\/case-studies/i,
        /behance\.net/i,
        /dribbble\.com/i,
        /\/case-study\//i,
        /\/project\//i,
      ];
      return validPatterns.some(pattern => pattern.test(value)) || parsed.hostname.length > 0;
    } catch {
      return false;
    }
  };

  const resetState = () => {
    setUrl("");
    setError("");
    setScanState("idle");
    setErrorDetails("");
    setProgress(0);
    setStage(1);
  };

  const handleBack = () => {
    resetState();
    onBack();
  };

  const handleTryAgain = () => {
    setScanState("idle");
    setErrorDetails("");
  };

  const handleCancelScan = () => {
    setScanState("idle");
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (error) {
      setError("");
    }
  };

  const handleContinue = async () => {
    setError("");
    setErrorDetails("");

    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }

    if (!validateURL(url)) {
      setError(
        "This doesn't seem to be a case study page. Make sure your URL leads directly to your project case study."
      );
      return;
    }

    setScanState("scanning");
    setProgress(1);
    setStage(1);

    // Start smooth progress simulation
    const progressInterval = setInterval(() => {
      setProgress((current) => {
        // Slow creep from current to 90% over time
        if (current < 90) {
          // Progressively slower increments as we approach 90%
          const increment = current < 30 ? 1.5 : current < 60 ? 0.8 : 0.4;
          return Math.min(90, current + increment);
        }
        return current;
      });
    }, 300); // Update every 300ms

    try {
      const result = await importCaseStudyFromUrl(url, {
        onProgress: (progressValue, stageValue) => {
          setProgress(progressValue);
          setStage(stageValue);
        },
      });
      // Clear the interval when API returns
      clearInterval(progressInterval);


      if (result.success && result.data) {
        setProgress(100);
        setStage(4);
        await new Promise(resolve => setTimeout(resolve, 800));
        setScanState("success");

        onImport(url);
        onImportSuccess?.(result.data);
        resetState();
      } else {
        setScanState("failed");
        setErrorDetails(result.error || "Failed to import case study");
      }
    } catch (err) {
      clearInterval(progressInterval);
      setScanState("failed");
      setErrorDetails(err instanceof Error ? err.message : "An unexpected error occurred");
    }
  };

  return (
    <>
      <ScanLoadingModal
        open={scanState === "scanning"}
        onCancel={handleCancelScan}
        progress={progress}
        stage={stage}
      />

      <ScanFailedModal
        open={scanState === "failed"}
        onTryAgain={handleTryAgain}
        errorMessage={errorDetails}
      />

      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Add Your Case Study URL</h2>
          <button
            onClick={handleBack}
            className="rounded-full p-1 hover:bg-gray-100 transition"
            aria-label="Close import form"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 text-sm">
          Paste the direct URL to your case study (e.g., portfolio.com/case-studies/project-name)
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="project-url" className="text-sm font-medium mb-2 block">
              Project URL
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <img src="/Sparkle.svg" alt=""  />
              </div>
              <div
                className={`rounded-lg p-[1px] ${
                  error
                    ? "bg-red-500"
                    : "bg-gradient-to-r from-[#6366F1] to-[#EF4444]"
                }`}
              >
                <Input
                  id="project-url"
                  type="url"
                  value={url}
                  onChange={handleUrlChange}
                  placeholder="https://guidionemachava.com/project/an-application-helps-merchants-in-selli..."
                  className={`pl-10 bg-white border-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-[#6366F1]/40 ${
                    error ? "focus-visible:ring-red-500" : ""
                  }`}
                />
              </div>
            </div>
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleBack} className="px-6">
              Back
            </Button>
            <Button type="button" onClick={handleContinue} className="px-6 bg-black hover:bg-gray-800 text-white">
              Continue
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export const ImportCaseStudyModal: React.FC<ImportCaseStudyModalProps> = ({
  open,
  onClose,
  onImport,
  onImportSuccess,
}) => {
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          handleClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[800px] bg-white rounded-lg px-20 py-10">
        <ImportCaseStudyForm onBack={handleClose} onImport={onImport} onImportSuccess={onImportSuccess} />
      </DialogContent>
    </Dialog>
  );
};
